import { Injectable, WritableSignal, computed, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { IReportJwtPayload } from '../utils/JwtUtils';
import { UserInfo } from '../models/app-models';

const TOKEN_KEY = 'token';
const USER_INFO_KEY = 'userInfo';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  // Token signal - source of truth for JWT
  public token: WritableSignal<string | null> = signal<string | null>(
    this.loadToken(),
  );

  // Decoded JWT - derived from token
  public jwt = computed<IReportJwtPayload | null>(() => {
    const token = this.token();
    if (token) {
      try {
        return jwtDecode<IReportJwtPayload>(token);
      } catch {
        return null;
      }
    }
    return null;
  });

  // UserInfo signal
  private userInfoSignal: WritableSignal<UserInfo | null> =
    signal<UserInfo | null>(this.loadUserInfo());

  public userInfo = this.userInfoSignal.asReadonly();

  private loadUserInfo(): UserInfo | null {
    const stored = sessionStorage.getItem(USER_INFO_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  private loadToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  // Token operations
  public setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    this.token.set(token);
  }

  public clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.token.set(null);
  }

  // UserInfo operations
  public setUserInfo(userInfo: UserInfo): void {
    sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
    this.userInfoSignal.set(userInfo);
  }

  public clearUserInfo(): void {
    sessionStorage.removeItem(USER_INFO_KEY);
    this.userInfoSignal.set(null);
  }

  // Clear all auth data (for logout)
  public clearAll(): void {
    this.clearToken();
    this.clearUserInfo();
  }
}
